<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading   = ref(false)
const saving    = ref(false)
const records   = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const summary   = ref({ total_pending: 0, total_approved: 0, total_amount: 0 })
const statusTab = ref('')
const search    = ref('')
const page      = ref(1)
const showModal = ref(false)
const formError = ref('')
const accounts  = ref([])

const blankForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  supplier: '',
  description: '',
  amount: '',
  expense_account_id: '',
  receipt: null,
})
const form = ref(blankForm())
const fileRef = ref(null)

const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (statusTab.value) params.status = statusTab.value
    if (search.value)    params.search = search.value
    const res = await axios.get('/accounting/petty-cash', { params })
    records.value = res.data.data.records
    summary.value = res.data.data.summary
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadMeta = async () => {
  try {
    const res = await axios.get('/accounting/active-ledgers')
    const raw = res.data.data ?? {}
    const all = Array.isArray(raw) ? raw : Object.values(raw).flat()
    accounts.value = all.filter(a => a.type === 'expense')
  } catch (e) {
    console.error('loadMeta failed', e)
  }
}

watch(statusTab, () => { page.value = 1; load() })
watch(page, load)
let st = null
const onSearch = () => { clearTimeout(st); st = setTimeout(() => { page.value = 1; load() }, 350) }
onMounted(() => { load(); loadMeta() })

const openCreate = () => {
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const onFileSelect = (e) => {
  form.value.receipt = e.target.files[0] ?? null
}

const saveRecord = async () => {
  if (!form.value.date || !form.value.amount) {
    formError.value = 'Date and amount are required.'
    return
  }
  try {
    saving.value = true
    formError.value = ''
    const fd = new FormData()
    Object.entries(form.value).forEach(([k, v]) => { if (v !== null && v !== '') fd.append(k, v) })
    if (form.value.receipt) fd.set('receipt', form.value.receipt)
    await axios.post('/accounting/petty-cash', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save record'
  } finally { saving.value = false }
}

const approve = async (id) => {
  if (!confirm('Approve this petty cash record? This will post Dr Expense / Cr Cash journal.')) return
  await axios.post(`/accounting/petty-cash/${id}/approve`)
  load()
}

const deleteRecord = async (id) => {
  if (!confirm('Delete this record?')) return
  await axios.delete(`/accounting/petty-cash/${id}`)
  load()
}

const statusClass = (s) => ({ pending: 'badge--orange', approved: 'badge--green', rejected: 'badge--red' }[s] ?? '')
const fmt = (v) => Number(v ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })
</script>

<template>
  <div>
    <Header />
    <div class="pc-page">

      <div class="pc-chips">
        <div class="pc-chip pc-chip--orange">
          <span class="chip-label">Pending</span>
          <span class="chip-val">{{ summary.total_pending }}</span>
        </div>
        <div class="pc-chip pc-chip--green">
          <span class="chip-label">Approved</span>
          <span class="chip-val">{{ summary.total_approved }}</span>
        </div>
        <div class="pc-chip pc-chip--blue">
          <span class="chip-label">Total Approved</span>
          <span class="chip-val">₦{{ fmt(summary.total_amount) }}</span>
        </div>
      </div>

      <div class="pc-toolbar">
        <div class="pc-tabs">
          <button :class="['pc-tab', statusTab === '' ? 'pc-tab--active' : '']" @click="statusTab = ''">All</button>
          <button :class="['pc-tab', statusTab === 'pending' ? 'pc-tab--active' : '']" @click="statusTab = 'pending'">Pending</button>
          <button :class="['pc-tab', statusTab === 'approved' ? 'pc-tab--active' : '']" @click="statusTab = 'approved'">Approved</button>
        </div>
        <div class="pc-toolbar-right">
          <input v-model="search" class="pc-search" placeholder="Search…" @input="onSearch" />
          <button class="btn-primary" @click="openCreate">+ New Record</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>PVC #</th>
                <th>Date</th>
                <th>Supplier</th>
                <th>Description</th>
                <th>Account</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="tbl-loading">Loading…</td></tr>
              <tr v-else-if="!records.data.length"><td colspan="8" class="tbl-empty">No records found</td></tr>
              <tr v-for="r in records.data" :key="r.id" class="data-row">
                <td><span class="pv-ref">{{ r.pvc_number }}</span></td>
                <td>{{ r.date }}</td>
                <td>{{ r.supplier ?? '—' }}</td>
                <td>{{ r.description ?? '—' }}</td>
                <td>{{ r.expense_account?.name ?? '—' }}</td>
                <td class="amt bold">{{ fmt(r.amount) }}</td>
                <td><span :class="['badge', statusClass(r.status)]">{{ r.status }}</span></td>
                <td>
                  <button v-if="r.status === 'pending'" class="btn-link" @click="approve(r.id)">Approve</button>
                  <button v-if="r.status !== 'approved'" class="btn-link btn-link--red" @click="deleteRecord(r.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="records.last_page > 1" class="pv-pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ records.last_page }}</span>
          <button :disabled="page >= records.last_page" @click="page++">›</button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>New Petty Cash Record</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Date *</label>
              <input type="date" v-model="form.date" />
            </div>
            <div class="form-group">
              <label>Amount *</label>
              <input type="number" v-model="form.amount" min="0" step="0.01" />
            </div>
            <div class="form-group">
              <label>Supplier</label>
              <input v-model="form.supplier" placeholder="Payee / supplier name" />
            </div>
            <div class="form-group">
              <label>Expense Account</label>
              <select v-model="form.expense_account_id">
                <option value="">— Default —</option>
                <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.code }} – {{ a.name }}</option>
              </select>
            </div>
            <div class="form-group form-group--full">
              <label>Description</label>
              <input v-model="form.description" placeholder="What was this for?" />
            </div>
            <div class="form-group form-group--full">
              <label>Receipt (optional)</label>
              <input type="file" ref="fileRef" accept="image/*,application/pdf" @change="onFileSelect" />
              <span v-if="form.receipt" class="file-name">{{ form.receipt.name }}</span>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="saveRecord">{{ saving ? 'Saving…' : 'Save Record' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pc-page { padding: 0 24px 40px; }
.pc-chips { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.pc-chip  { display: flex; flex-direction: column; align-items: center; padding: 12px 20px; border-radius: 8px; min-width: 120px; }
.pc-chip--orange { background: #fff8ee; }
.pc-chip--green  { background: #edf7f2; }
.pc-chip--blue   { background: #eef4ff; }
.chip-label { font-size: 12px; color: #888; margin-bottom: 4px; }
.chip-val   { font-size: 20px; font-weight: 700; color: #1a2a45; }
.pc-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 12px; flex-wrap: wrap; }
.pc-tabs    { display: flex; gap: 4px; }
.pc-tab     { padding: 7px 14px; border: 1px solid #dde3ef; border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer; color: #555; }
.pc-tab--active { background: #004AAD; color: #fff; border-color: #004AAD; }
.pc-toolbar-right { display: flex; gap: 10px; align-items: center; }
.pc-search  { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; width: 200px; }
.table-card  { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.table-scroll { overflow-x: auto; }
.data-table  { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.data-row:hover td { background: #fafbff; }
.tbl-loading, .tbl-empty { text-align: center; color: #999; padding: 40px; }
.amt { text-align: right; font-variant-numeric: tabular-nums; }
.bold { font-weight: 600; }
.pv-ref { font-weight: 600; color: #004AAD; }
.pv-pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pv-pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pv-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
.badge         { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--orange { background: #fff3cd; color: #856404; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--red    { background: #f8d7da; color: #842029; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 600px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
.file-name  { font-size: 12px; color: #555; margin-top: 4px; }
</style>
